import { DictType } from "@pma/types";
import { toast } from "@hooks/use-toast";
import useTranslator from "@hooks/use-translator";
import { useAuth } from "@stores/auth-store";
import { useMutation } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useState } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@components/common/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getOrganizationGlobalSearch } from "@pma/utils/utils";

const formSchema = z.object({
  search_text: z.string().min(2, { message: "Please enter file name" }),
});

type SearchOptions = {
  pending: boolean;
  text: string;
  results: {
    items: DictType[];
  };
};

interface Props {
  search: SearchOptions;

  setSearch: Dispatch<SetStateAction<any>>;
}
const SearchFile = ({ search, setSearch }: Props) => {
  const { translate } = useTranslator();
  const { authConfig } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      search_text: "",
    },
  });

  const orgId = authConfig?.last_organization_id;

  const globalSearchMutation = useMutation({
    mutationKey: ["global-search", search.text],
    mutationFn: () =>
      getOrganizationGlobalSearch(orgId, search?.text, {
        in_files: 1,
        in_projects: 0,
        in_notes: 0,
        in_tasks: 0,
      }),
    onSuccess: (data) => {
      setSearch((prev: SearchOptions) => ({
        ...prev,
        pending: false,
        results: data,
      }));
    },
    onError: (error: DictType) => {
      const serverError = error?.response?.data?.message;
      const emptyResponse = "Incorrect page index";
      const message =
        serverError === emptyResponse ? "No items found" : serverError;

      message &&
        toast({
          title: translate("Error"),
          description: translate(message),
          variant: "destructive",
        });
    },
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    globalSearchMutation.mutate();
  }

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="text-white">
          <FormField
            control={form.control}
            name="search_text"
            render={({ field }) => (
              <FormItem className="my-3 px-2 flex flex-col items-start justify-start space-y-2">
                <FormControl>
                  <input
                    placeholder={translate("Search file")}
                    className="focus:outline-none shadow-none placeholder:text-xs bg-none border-0 text-lg font-medium w-[140px] md:w-[200px] border-b-primary rounded-none border-b-2 text-black"
                    onChange={(e) => {
                      field.onChange(e);

                      if (e.target.value === "") {
                        setSearch((prev: SearchOptions) => ({
                          ...prev,
                          results: {
                            items: [],
                          },
                          pending: false,
                        }));
                      }

                      setSearch((prev: SearchOptions) => ({
                        ...prev,
                        text: e.target.value,
                        pending: true,
                      }));
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
};

export default SearchFile;
